// Shared JointJS bootstrap (ported from the prototype's lib/joint.ts — the hard-won interop, reused, not
// relitigated). @joint/core ships CJS/UMD with no CSS; normalise the import and mount only once the
// container has real dimensions (the blank-canvas race).
import * as JointModule from '@joint/core'

const mod: any = JointModule
export const joint: any = mod.dia ? mod : (mod.default ?? mod)
export const dia = joint.dia
export const shapes = joint.shapes
export const V = joint.V

/** The cell namespace every paper resolves `type` against: the library's shapes plus ours, in a plain
 * object we own. Never add to `joint.shapes` itself — under Vite's dev server that namespace is a normal
 * object, but in the Rollup production bundle it is frozen, and the assignment throws at import time and
 * white-screens the whole app (prod, 2026-09-10). Register with `defineCell`. */
export const cells: any = { ...joint.shapes, scout: {} }

/** Registers a custom element under `scout.<name>` in our namespace and returns it. */
export function defineCell<T>(name: string, ctor: T): T {
  cells.scout[name] = ctor
  return ctor
}

/** Scale + centre all graph content within a w×h paper (manual; no reliance on internal fit). */
export function fitContent(paper: any, model: any, w: number, h: number, pad = 30): void {
  const bbox = model.getBBox()
  if (!bbox || !bbox.width || !bbox.height) return
  const s = Math.max(0.2, Math.min((w - pad * 2) / bbox.width, (h - pad * 2) / bbox.height, 1.25))
  paper.scale(s)
  paper.translate(Math.round((w - bbox.width * s) / 2 - bbox.x * s), Math.round((h - bbox.height * s) / 2 - bbox.y * s))
}

/** The paper transform after zooming by `factor` about the paper pixel (px, py): the paper-local point under
 * the pointer before is still under it after, and the scale stays within [min, max]. Pure, so the wheel
 * handler is one call and the promise ("the point under the cursor stays under the cursor") is unit-tested. */
export function zoomAbout(scale: number, tx: number, ty: number, px: number, py: number, factor: number, min = 0.2, max = 3): { scale: number; tx: number; ty: number } {
  const s = Math.max(min, Math.min(max, scale * factor))
  if (s === scale) return { scale, tx, ty } // factor 1, or already at the clamp: no drift from the arithmetic
  const lx = (px - tx) / scale; const ly = (py - ty) / scale
  return { scale: s, tx: px - lx * s, ty: py - ly * s }
}

/** Observe `el` until it has a real size, then build(w,h) once; onResize(w,h) afterwards. Returns a disconnect. */
export function onSized(el: HTMLElement, build: (w: number, h: number) => void, onResize?: (w: number, h: number) => void): () => void {
  let built = false
  const ro = new ResizeObserver((entries) => {
    const r = entries[0].contentRect
    const w = Math.round(r.width); const h = Math.round(r.height)
    if (w < 20 || h < 20) return
    if (!built) { built = true; build(w, h) } else onResize?.(w, h)
  })
  ro.observe(el)
  return () => ro.disconnect()
}
